namespace $ {
	export type $shm_projects_task_action = 'research' | 'work' | 'work_prerelease'
	export type $shm_projects_goal_project_summary = Pick< $shm_projects_data_record,
		'id' | 'title' | 'type' | 'archived' | 'parent_id' | 'version' | 'version_date'
	>

	export type $shm_projects_goal_timings = {
		workspace_ms: number
		builder_ms: number
		clarification_ms: number
		waiting_ms: number
		implementation_ms: number
		visual_review_ms: number
		checkpoint_ms: number
		prerelease_ms: number
		deployment_ms: number
	}

	export type $shm_projects_goal_run = {
		id: string
		project_id: string
		initiator?: {
			profile_id: string
			profile_name: string
		}
		task_id?: string
		task_mode?: 'plan' | 'research' | 'work'
		task_action?: $shm_projects_task_action
		task_revision?: number
		project?: $shm_projects_goal_project_summary
		goal: string
		refinements: readonly string[]
		status: 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'stopped'
		phase: string
		started_at: number
		iteration_started_at?: number
		updated_at: number
		completed_at: number | null
		elapsed_ms: number
		timings?: $shm_projects_goal_timings
		iteration: number
		session_id: string
		summary: string
		error: string
		recovery_attempts?: number
		deployment_recovery_attempts?: number
		deployment_error?: string
		benchmark?: {
			status: 'running' | 'done' | 'failed'
			version: string
			started_at: number
			completed_at?: number
			error?: string
		}
		worker_pid?: number
		workspace?: {
			id: string
			active: boolean
		}
		prerelease_base_available?: boolean
		operation?: 'clarification' | 'goal' | 'prerelease'
		development?: {
			id: string
			source_project_id: string
			source_project_title: string
			status: $shm_projects_goal_run['status']
			busy: boolean
			phase: string
			updated_at: number
			changed_paths: readonly string[]
			changed_modules: readonly string[]
			affected_modules: readonly string[]
			projects: readonly {
				project_id: string
				reason: string
			}[]
		}
		clarifications?: readonly {
			id: string
			question: string
			answer: string
		}[]
		events: readonly {
			id?: string
			sequence?: number
			time: number
			phase: string
			text: string
			kind?: 'message' | 'reasoning' | 'system'
			iteration?: number
			scope?: 'iteration' | 'prerelease'
		}[]
		prerelease?: {
			status: 'queued' | 'running' | 'done' | 'failed' | 'stopped'
			started_at: number
			completed_at: number | null
			summary: string
			error: string
			events: readonly {
				id?: string
				sequence?: number
				time: number
				phase: string
				text: string
				kind?: 'message' | 'reasoning' | 'system'
				iteration?: number
				scope?: 'iteration' | 'prerelease'
			}[]
			results: readonly {
				title: string
				description: string
				screenshots: readonly string[]
				demos?: readonly string[]
			}[]
			testing?: boolean
			kind?: 'fast' | 'full'
			full_reason?: string
			artifacts: readonly {
				type: 'screenshot' | 'video' | 'report'
				path: string
			}[]
			external_error?: string
		external_error_accepted_at?: number
		stages?: readonly $shm_projects_data_stage[]
		}
		results?: readonly {
			title: string
			description: string
			screenshots: readonly string[]
			demos?: readonly string[]
		}[]
		result_history?: readonly {
			iteration: number
			summary: string
			clarifications?: readonly {
				id: string
				question: string
				answer: string
			}[]
			started_at?: number
			completed_at: number
			elapsed_ms?: number
			timings?: $shm_projects_goal_timings
			artifacts?: readonly {
				type: 'screenshot' | 'video' | 'report'
				path: string
			}[]
			changes: readonly {
				title: string
				description: string
				screenshots: readonly string[]
				demos?: readonly string[]
			}[]
		}[]
			repositories?: readonly {
				path: string
				branch: string
				base_branch: string
				base_commit: string
				initial_status?: readonly string[]
			}[]
		checkpoints?: readonly {
			iteration: number
			commits: Record< string, string >
		}[]
		artifacts: readonly {
			type: 'screenshot' | 'video' | 'report'
			path: string
		}[]
		deployments: readonly {
			stage: 'Пререлиз' | 'Релиз'
			status: 'planned' | 'deployed' | 'failed'
			driver: 'docker'
			target: string
			server: string
			context: string
			image: string
			execute: boolean
			created_at: number
			from_version?: string
			channel_project_id?: string
			version: string
			delivery_project_id?: string
			from_delivery_version?: string
			delivery_version?: string
			date: string
			changes: string
			record: $shm_projects_data_version
			steps: readonly string[]
			artifact?: {
				id: string
				sha256: string
				manifest: string
				bytes: number
				files: number
			}
			components?: readonly $shm_projects_release_component_artifact[]
			deployed_at?: number
			finalized_at?: number
			error?: string
			stages?: readonly $shm_projects_data_stage[]
		}[]
	}

	export type $shm_projects_goal_project_create = {
		project: $shm_projects_data_record
		projects: readonly $shm_projects_data_record[]
	}

	export type $shm_projects_goal_project_config_update = $shm_projects_goal_project_create & {
		config: string
	}

	export type $shm_projects_session_context = {
		cwd: string
		instructions: string
		files: readonly string[]
	}

	export type $shm_projects_task_record = {
		id: string
		legacy_id?: string
		project_id: string
		title: string
		description: string
		result: string
		run_id: string
		status: $shm_projects_goal_run['status'] | 'draft'
		creator_id: string
		creator_name: string
		assignee_id: string
		assignee_name: string
		acceptance_criteria: string
		priority: 'low' | 'normal' | 'high' | 'urgent'
		revision: number
		planned_revision: number
		mode: 'plan' | $shm_projects_task_action
		pinned?: boolean
		archived?: boolean
		created_at: number
		updated_at: number
	}

	export class $shm_projects_goal_api extends $mol_object2 {

		static initiator_provider: ()=> null | {
			profile_id: string
			profile_name: string
			github_token?: string
		} = ()=> null

		static base() {
			return this.$.$shm_projects_config.projects_api_url()
		}

		static async json( body?: object, id = '', project_id = '' ) {
			const url = new URL( this.base() )
			if( id ) url.searchParams.set( 'id', id )
			if( project_id ) url.searchParams.set( 'project_id', project_id )
			const token = this.$.$shm_projects_config.agent_token()
			const headers: Record< string, string > = {}
			if( body ) headers[ 'Content-Type' ] = 'application/json'
			if( token ) headers[ 'X-Hitalama-Agent-Token' ] = token
			const action_body = body as { action?: unknown, id?: unknown } | undefined
			const action = String( action_body?.action || '' )
			const session_request = Boolean( id || action_body?.id ) || [ 'start', 'task_run', 'variant_create' ].includes( action )
			const initiator = body && session_request ? this.initiator_provider() : null
			const request_body = body && initiator ? { ... body, initiator } : body
			const response = await fetch( url, request_body ? {
				method: 'POST',
				headers,
				body: JSON.stringify( request_body ),
			} : { cache: 'no-store', headers } )
			const data = await response.json()
			if( !response.ok || ( data?.error && !data?.id ) ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
			return data
		}

		static start( project_id: string, goal: string, ask_questions = false, attachments: readonly $shm_projects_goal_attachment[] = [] ) {
			return this.json({ action: 'start', project_id, goal, ask_questions, attachments }) as Promise< $shm_projects_goal_run >
		}

		static project_create( title: string ) {
			return this.json({ action: 'project_create', title }) as Promise< $shm_projects_goal_project_create >
		}

		static project_archive( project_id: string ) {
			return this.json({ action: 'project_archive', project_id }) as Promise< $shm_projects_goal_project_create >
		}

		static project_delete( project_id: string ) {
			return this.json({ action: 'project_delete', project_id }) as Promise< $shm_projects_goal_project_create >
		}

		static project_rename( project_id: string, title: string ) {
			return this.json({ action: 'project_rename', project_id, title }) as Promise< $shm_projects_goal_project_create >
		}

		static project_product_update( project_id: string, product: $shm_projects_data_product ) {
			return this.json({ action: 'project_product_update', project_id, product }) as Promise< $shm_projects_goal_project_create >
		}

		static project_config_update( project_id: string, config: string ) {
			return this.json({ action: 'project_config_update', project_id, config }) as Promise< $shm_projects_goal_project_config_update >
		}

		static session_context( project_id: string ) {
			const url = new URL( this.base() )
			url.searchParams.set( 'session_context', project_id )
			const token = this.$.$shm_projects_config.agent_token()
			return fetch( url, {
				cache: 'no-store',
				headers: token ? { 'X-Hitalama-Agent-Token': token } : {},
			}).then( async response => {
				const data = await response.json()
				if( !response.ok || data?.error ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
				return data as $shm_projects_session_context
			})
		}

		static task_run( project_id: string, task_id: string, run_id: string, revision: number, task: Partial< $shm_projects_task_record >, task_action: $shm_projects_task_action ) {
			return this.json({ action: 'task_run', project_id, task_id, run_id, revision, task, task_action }) as Promise< $shm_projects_goal_run >
		}

		static tasks( project_id: string ) {
			const url = new URL( this.base() )
			url.searchParams.set( 'tasks', project_id )
			const token = this.$.$shm_projects_config.agent_token()
			return fetch( url, {
				cache: 'no-store',
				headers: token ? { 'X-Hitalama-Agent-Token': token } : {},
			} ).then( async response => {
				const data = await response.json()
				if( !response.ok || !Array.isArray( data ) ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
				return data as readonly $shm_projects_task_record[]
			} )
		}

		static release_audit() {
			const url = new URL( this.base() )
			url.searchParams.set( 'release_audit', '1' )
			const token = this.$.$shm_projects_config.agent_token()
			return fetch( url, { cache: 'no-store', headers: token ? { 'X-Hitalama-Agent-Token': token } : {} } )
				.then( async response => {
					const data = await response.json()
					if( !response.ok || !Array.isArray( data ) ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
					return data as readonly {
						project_id: string
						version: string
						manifest_version: string
						version_file: string
						artifact_version: string
						release_set: boolean
						consistent: boolean
						issues: readonly string[]
					}[]
				} )
		}

		static release_reconcile( apply = false ) {
			return this.json({ action: 'release_reconcile', apply }) as Promise< {
				applied: boolean
				projects: readonly { project_id: string, from_version: string, version: string }[]
				audit: Awaited< ReturnType< typeof $shm_projects_goal_api.release_audit > >
			} >
		}

		static release_storage_audit() {
			const url = new URL( this.base() )
			url.searchParams.set( 'release_storage_audit', '1' )
			const token = this.$.$shm_projects_config.agent_token()
			return fetch( url, { cache: 'no-store', headers: token ? { 'X-Hitalama-Agent-Token': token } : {} } )
				.then( async response => {
					const data = await response.json()
					if( !response.ok || data?.error ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
					return data as $shm_projects_release_storage_audit_report
				} )
		}

		static artifact_storage_audit() {
			const url = new URL( this.base() )
			url.searchParams.set( 'artifact_storage_audit', '1' )
			const token = this.$.$shm_projects_config.agent_token()
			return fetch( url, { cache: 'no-store', headers: token ? { 'X-Hitalama-Agent-Token': token } : {} } )
				.then( async response => {
					const data = await response.json()
					if( !response.ok || data?.error ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
					return data as $shm_projects_record_artifact_audit_report
				} )
		}

		static artifact_storage_retention() {
			const url = new URL( this.base() )
			url.searchParams.set( 'artifact_storage_retention', '1' )
			const token = this.$.$shm_projects_config.agent_token()
			return fetch( url, { cache: 'no-store', headers: token ? { 'X-Hitalama-Agent-Token': token } : {} } )
				.then( async response => {
					const data = await response.json()
					if( !response.ok || data?.error ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
					return data as $shm_projects_record_artifact_retention_report
				} )
		}

		static artifact_storage_prune( fingerprint: string ) {
			return this.json({ action: 'artifact_storage_prune', fingerprint, apply: true }) as Promise< $shm_projects_record_artifact_retention_report >
		}

		static artifact_storage_restore( fingerprint: string ) {
			return this.json({ action: 'artifact_storage_restore', fingerprint, apply: true }) as Promise< $shm_projects_record_artifact_restore_report >
		}

		static release_storage_prune( apply = false ) {
			return this.json({ action: 'release_storage_prune', apply }) as Promise< $shm_projects_release_storage_prune_report >
		}

		static project_history_externalize( project_id: string, apply = false, history_file = '' ) {
			return this.json({ action: 'project_history_externalize', project_id, apply, history_file }) as Promise< {
				project_id: string
				root: string
				history_file: string
				history_records: number
				manifest_bytes_before: number
				manifest_bytes_after: number
				applied: boolean
				already_external: boolean
				backup_file?: string
			} >
		}

		static project_prerelease( project_id: string, changes: string, requested: 'fast' | 'full' | boolean = 'fast' ) {
			const kind = requested === true ? 'full' : requested === false ? 'fast' : requested
			return this.json({ action: 'project_prerelease', project_id, changes, kind, testing: kind === 'full' }) as Promise< $shm_projects_goal_run >
		}

		static benchmark_run( project_id: string, version: string ) {
			return this.json({ action: 'benchmark_run', project_id, version }) as Promise< $shm_projects_goal_run >
		}

		static dependency_refresh( project_id: string, repository: string ) {
			return this.json({ action: 'dependency_refresh', project_id, repository }) as Promise< {
				local: string
				latest: string
				branch: string
				behind: number | null
				ahead: number | null
				url: string
			} >
		}

		static task_create( task: $shm_projects_task_record ) {
			return this.json({ action: 'task_create', project_id: task.project_id, task }) as Promise< $shm_projects_task_record >
		}

		static task_update( project_id: string, task_id: string, change: Partial< $shm_projects_task_record > ) {
			return this.json({ action: 'task_update', project_id, task_id, change }) as Promise< $shm_projects_task_record >
		}

		static task_message( id: string, message: string ) {
			return this.json({ action: 'task_message', id, message }) as Promise< $shm_projects_goal_run >
		}

		static task_prerelease( id: string, changes: string, requested: 'fast' | 'full' | boolean = 'fast' ) {
			const kind = requested === true ? 'full' : requested === false ? 'fast' : requested
			return this.json({ action: 'task_prerelease', id, changes, kind, testing: kind === 'full' }) as Promise< $shm_projects_goal_run >
		}

		static refine( id: string, goal: string, ask_questions = false, attachments: readonly $shm_projects_goal_attachment[] = [] ) {
			return this.json({ action: 'refine', id, goal, ask_questions, attachments }) as Promise< $shm_projects_goal_run >
		}

		static clarify( id: string, answers: readonly { id: string, answer: string }[] ) {
			return this.json({ action: 'clarify', id, answers }) as Promise< $shm_projects_goal_run >
		}

		static stop( id: string ) {
			return this.json({ action: 'stop', id }) as Promise< $shm_projects_goal_run >
		}

		static continue( id: string ) {
			return this.json({ action: 'continue', id }) as Promise< $shm_projects_goal_run >
		}

		static rollback( id: string, iteration: number ) {
			return this.json({ action: 'rollback', id, iteration }) as Promise< $shm_projects_goal_run >
		}

		static run( id: string ) {
			return this.json( undefined, id ) as Promise< $shm_projects_goal_run >
		}

		static active( project_id: string ) {
			return this.json( undefined, '', project_id ) as Promise< $shm_projects_goal_run | null >
		}

		static deploy( id: string, action: 'prerelease' | 'release', changes: string, requested: 'fast' | 'full' | boolean = 'fast' ) {
			const kind = requested === true ? 'full' : requested === false ? 'fast' : requested
			return this.json({ action, id, changes, kind, testing: action === 'prerelease' && kind === 'full' }) as Promise< $shm_projects_goal_run >
		}

		static prerelease_retry( id: string ) {
			return this.json({ action: 'prerelease_retry', id }) as Promise< $shm_projects_goal_run >
		}

		static prerelease_accept_external( id: string ) {
			return this.json({ action: 'prerelease_accept_external', id }) as Promise< $shm_projects_goal_run >
		}

		static version_activate( project_id: string, version: string, stage: 'prerelease' | 'release' ) {
			return this.json({ action: 'version_activate', project_id, version, stage }) as Promise< $shm_projects_goal_run >
		}

	}

	export class $shm_projects_goal_api_events extends $mol_object {

		private socket: WebSocket | null = null
		private reconnect_timer: ReturnType< typeof setTimeout > | null = null
		private heartbeat_timer: ReturnType< typeof setTimeout > | null = null
		private response_timer: ReturnType< typeof setTimeout > | null = null
		private destroyed = false
		private current_run: $shm_projects_goal_run | null = null

		id() { return '' }
		notify() { return ( run: $shm_projects_goal_run )=> {} }
		heartbeat_delay() { return 15_000 }
		response_delay() { return 5_000 }

		private timer_clear( field: 'heartbeat_timer' | 'response_timer' ) {
			const timer = this[ field ]
			if( timer ) clearTimeout( timer )
			this[ field ] = null
		}

		private heartbeat_schedule() {
			this.timer_clear( 'heartbeat_timer' )
			if( this.destroyed ) return
			this.heartbeat_timer = setTimeout( ()=> {
				this.heartbeat_timer = null
				this.subscribe()
			}, this.heartbeat_delay() )
		}

		private subscribe() {
			const socket = this.socket
			if( !socket || socket.readyState !== socket.OPEN ) return
			socket.send( JSON.stringify({ action: 'subscribe', id: this.id(), stream: 1 }) )
			this.timer_clear( 'response_timer' )
			this.response_timer = setTimeout( ()=> {
				this.response_timer = null
				socket.close()
			}, this.response_delay() )
		}

		native() {
			if( this.socket ) return this.socket
			const url = new URL( $shm_projects_goal_api.base() )
			url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
			const token = this.$.$shm_projects_config.agent_token()
			if( token ) url.searchParams.set( 'token', token )
			const socket = this.socket = new this.$.$mol_dom_context.WebSocket( url, 'shm-projects-goal' )
			socket.onopen = ()=> this.subscribe()
			socket.onmessage = event => $mol_wire_async( this ).message( event )
			socket.onerror = ()=> {}
			socket.onclose = ()=> $mol_wire_async( this ).closed()
			return socket
		}

		message( event: MessageEvent ) {
			if( typeof event.data !== 'string' ) return
			try {
				this.timer_clear( 'response_timer' )
				this.heartbeat_schedule()
				const data = JSON.parse( event.data ) as any
				let run: $shm_projects_goal_run
				if( data?.type === 'run_patch' ) {
					const current = this.current_run
					if( !current ) {
						this.socket?.close()
						return
					}
					const patch = data.run as Partial< $shm_projects_goal_run > & { events?: $shm_projects_goal_run['events'], prerelease?: $shm_projects_goal_run['prerelease'] | null }
					const base = { ...current } as Record< string, any >
					for( const key of data.unset ?? [] ) delete base[ String( key ) ]
					const events = $shm_projects_record_event_stream_merge(
						current.events,
						patch.events ?? [],
						data.events?.reset === true,
						Number( data.events?.count ?? current.events.length ),
					)
					const prerelease = patch.prerelease === null ? undefined : patch.prerelease
						? {
							...( current.prerelease ?? patch.prerelease ),
							...patch.prerelease,
							events: $shm_projects_record_event_stream_merge(
								current.prerelease?.events ?? [],
								patch.prerelease.events ?? [],
								data.prerelease_events?.reset === true,
								Number( data.prerelease_events?.count ?? current.prerelease?.events.length ?? 0 ),
							),
						}
						: current.prerelease
					if( prerelease ) {
						for( const key of data.prerelease_events?.unset ?? [] ) delete ( prerelease as Record< string, any > )[ String( key ) ]
					}
					run = { ...base, ...patch, events, prerelease } as $shm_projects_goal_run
				} else {
					run = data as $shm_projects_goal_run
				}
				this.current_run = run
				this.notify()( run )
			} catch( error ) {
				$mol_fail_log( error )
			}
		}

		closed() {
			this.timer_clear( 'heartbeat_timer' )
			this.timer_clear( 'response_timer' )
			this.socket = null
			this.current_run = null
			if( this.destroyed || this.reconnect_timer ) return
			this.reconnect_timer = setTimeout( ()=> {
				this.reconnect_timer = null
				if( !this.destroyed ) this.native()
			}, 1000 )
		}

		override destructor() {
			this.destroyed = true
			this.timer_clear( 'heartbeat_timer' )
			this.timer_clear( 'response_timer' )
			if( this.reconnect_timer ) clearTimeout( this.reconnect_timer )
			this.reconnect_timer = null
			const socket = this.socket
			this.socket = null
			if( socket ) {
				socket.onopen = null
				socket.onmessage = null
				socket.onerror = null
				socket.onclose = null
				socket.close()
			}
			super.destructor()
		}

	}

}
